import { useCallback } from 'react';
import type { MutableRefObject } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import {
  BorrowerType,
  type TFinancialConditionCertificateResponse,
  type TFinancialConditionFormValues,
} from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/types.ts';
import { FINANCIAL_CONDITION_VALIDATION_MESSAGES } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/constants.ts';
import {
  getMissingRequiredFinancialStatementFieldNames,
  getRequiredFinancialStatementErrorFieldNames,
} from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/utils.ts';
import { focusDocumentGenerationField } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/validation.ts';
import type { TCertificateSaveResult } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/types.ts';

type TUseFinancialConditionFormSaveParams = {
  form: UseFormReturn<TFinancialConditionFormValues>;
  certificateVersionRef: MutableRefObject<number | null>;
  onSaveFinancialCondition?: (
    values: TFinancialConditionFormValues,
    version: number
  ) => Promise<TCertificateSaveResult>;
  onLoadFinancialCondition?: () => Promise<TFinancialConditionCertificateResponse | null>;
};

export const useFinancialConditionFormSave = ({
  form,
  certificateVersionRef,
  onSaveFinancialCondition,
  onLoadFinancialCondition,
}: TUseFinancialConditionFormSaveParams) =>
  useCallback(
    async (values: TFinancialConditionFormValues) => {
      if (!onSaveFinancialCondition) {
        return;
      }

      if (!values.reportDate) {
        form.setError('reportDate', {
          type: 'required',
          message: FINANCIAL_CONDITION_VALIDATION_MESSAGES.reportDate,
        });
        focusDocumentGenerationField('reportDate');
        return;
      }

      if (
        values.borrowerType?.value === BorrowerType.LegalEntity &&
        !values.financialStatementForm
      ) {
        form.setError('financialStatementForm', {
          type: 'required',
          message: FINANCIAL_CONDITION_VALIDATION_MESSAGES.financialStatementForm,
        });
        focusDocumentGenerationField('financialStatementForm');
        return;
      }

      form.clearErrors(['reportDate', 'financialStatementForm']);

      const errorFieldNames = getRequiredFinancialStatementErrorFieldNames(values);
      const missingRowPaths = getMissingRequiredFinancialStatementFieldNames(values);
      const missingFieldNames = errorFieldNames.filter((fieldName) =>
        missingRowPaths.some((rowPath) => fieldName.startsWith(`${rowPath}.`))
      );

      form.clearErrors(errorFieldNames);

      if (missingFieldNames.length) {
        missingFieldNames.forEach((fieldName) => {
          form.setError(fieldName, {
            type: 'required',
            message: FINANCIAL_CONDITION_VALIDATION_MESSAGES.requiredTemplateRow,
          });
        });

        const firstFieldName = missingFieldNames[0];
        if (firstFieldName) {
          focusDocumentGenerationField(firstFieldName);
        }

        return;
      }

      const version = certificateVersionRef.current;
      if (version === null) {
        return;
      }

      const result = await onSaveFinancialCondition(values, version);
      if (result !== 'success') {
        return;
      }

      form.reset(values);

      void onLoadFinancialCondition?.().then((response) => {
        if (response) {
          certificateVersionRef.current = response.version;
        }
      });
    },
    [certificateVersionRef, form, onLoadFinancialCondition, onSaveFinancialCondition]
  );
